/* eslint-disable graphql/template-strings */
import { Injectable } from '@angular/core';
import gql from 'graphql-tag';
import { first } from 'rxjs/operators';
import { isNotNullOrUndefined, GraphqlApiService, GetLastAggregateQuery, Series } from 'ngx-core-services';
import {
  AuthenticationAPISolarmanResponse,
  CallAPISolarmanDeviceInfoResponse,
  CallAPISolarmanModbusResponse,
  CallPROSolarmanDeviceDetailsResponse,
  CallPROSolarmanFirmwareDetailsResponse,
  CallPROSolarmanLastActionResponse,
  CallPROSolarmanUpgradeFirmwareResponse,
} from 'ngx-modbus';

import { SolarmanKeys } from './solarman.env';

const SOLARMAN_DEBUG = { ENABLED: false };
const smLog = (msg: string, data?: unknown) => SOLARMAN_DEBUG.ENABLED && console.log(`[Modbus:Solarman] ${msg}`, data ?? '');

const AUTH = SolarmanKeys;

@Injectable({
  providedIn: 'root',
})
export class SolarmanService {
  readonly client$ = this.graphqlApiService.client$.pipe(
    isNotNullOrUndefined(),
  );

  private credentials?: AuthenticationAPISolarmanResponse;
  private credentialsRequest?: Promise<AuthenticationAPISolarmanResponse>;

  constructor(
    private readonly getLastAggregateQuery: GetLastAggregateQuery,
    private readonly graphqlApiService: GraphqlApiService,
  ) { }

  /**
   * Single-flight: concurrent callers share one token request. On first page
   * entry several services authenticate in parallel — without this, multiple
   * tokens get issued and calls in flight can end up with a superseded one.
   */
  async authenticate(): Promise<AuthenticationAPISolarmanResponse> {
    if (this.credentials) {
      smLog('authenticate: using cached credentials');
      return this.credentials;
    }
    if (this.credentialsRequest) {
      smLog('authenticate: joining in-flight request');
      return this.credentialsRequest;
    }
    this.credentialsRequest = this.fetchCredentials().finally(() => {
      this.credentialsRequest = undefined;
    });
    return this.credentialsRequest;
  }

  private async fetchCredentials(): Promise<AuthenticationAPISolarmanResponse> {
    smLog('authenticate: fetching new credentials');

    const client = await this.client$.pipe(first()).toPromise();

    const passwordQuery = gql`
      query {
        getSolarmanPortalPassword {
          errorCode
          errorMessage
          password
        }
      }
    `;

    const { data: passwordData } = await client.query<{ getSolarmanPortalPassword: { errorCode: number; errorMessage: string; password: string } }>({
      query: passwordQuery,
      fetchPolicy: 'network-only',
    });

    const password = passwordData.getSolarmanPortalPassword.password;

    const url = '/account/v1.0/token';
    const query = gql`
      query authenticateAPISolarman($url: String!, $body: AWSJSON, $queryParams: AWSJSON) {
        authenticateAPISolarman(url: $url, body: $body, queryParams: $queryParams)
      }
    `;

    const fetchPolicy = 'network-only';
    const { data } = await client.query<{ [key: string ]: Array<Partial<object>> }>({
      query,
      fetchPolicy,
      variables: {
        url,
        body: JSON.stringify({
          appSecret: AUTH.appSecret,
          password,
          orgId: AUTH.orgId,
          username: AUTH.username,
        }),
        queryParams: JSON.stringify({
          appId: AUTH.appId,
          language: AUTH.language,
        }),
      },
    });

    const result = data.authenticateAPISolarman as unknown as string;
    this.credentials = JSON.parse(result) as AuthenticationAPISolarmanResponse;
    // Never log the raw response or any token fragment — credentials end up in every user's console.
    smLog('authenticate result ' + JSON.stringify({
      keys: Object.keys(this.credentials ?? {}),
      hasToken: !!this.credentials?.access_token,
    }));
    return this.credentials;
  }

  async deviceInfoRequest(serial: string) {
    const url = '/device/v1.0/communication';
    const body = {
      deviceSn: serial,
    };

    const result = await this.callApiSolarman(url, body);
    const response = JSON.parse(result) as CallAPISolarmanDeviceInfoResponse;
    if (response.success === false) {
      throw new Error('Device not found');
    }
    return response;
  }

  async deviceLastActionRequest(deviceId: string, orderType: number = 2) {
    const url = `/order-s/order/action/last`;
    const queryParams = {
      language: AUTH.language,
      deviceId,
      orderType,
    };

    const result = await this.callApiSolarman(url, {}, 'callPROSolarman', 'GET', queryParams);
    return JSON.parse(result) as CallPROSolarmanLastActionResponse;
  }

  async productFirmwareListRequest(productId: string) {
    const url = `/order-s/order/firmware/listName/${productId}`;

    const result = await this.callApiSolarman(url, {}, 'callPROSolarman', 'GET');
    return JSON.parse(result) as CallPROSolarmanFirmwareDetailsResponse[];
  }

  async deviceUpgradeRequest(
    deviceId: number,
    deviceSn: string,
    firmwareId: number,
    gatewayId: number,
    gatewaySn: string,
    orderTimeout: number,
    product: string,
  ) {
    const url = '/order-s/order/action/upgrade/send';
    const body = {
      deviceId,
      deviceSn,
      firmwareId,
      gatewayId,
      gatewaySn,
      orderTimeout,
      product,
    };

    const result = await this.callApiSolarman(url, body, 'callPROSolarman');
    return JSON.parse(result) as CallPROSolarmanUpgradeFirmwareResponse;
  }

  async deviceDetailsRequest(deviceId: string) {
    const url = '/device-s/device/detail';
    const queryParams = {
      language: AUTH.language,
      deviceId,
      needRealTimeDataFlag: true,
    };

    const result = await this.callApiSolarman(url, queryParams, 'callPROSolarman');
    return JSON.parse(result) as CallPROSolarmanDeviceDetailsResponse;
  }

  async addLoggerToSolarmanByInverterSerial(serial: string) {
    const query = gql`
      fragment aggregatevaluestype on AggregateValuesType {
        TS
        GwId
      }
    `;


    const response = await this.getLastAggregateQuery.execute(
      query,
      {
        serial,
        series: Series.DAY,
        type: 'Solarman',
      },
    );

    const data = response.data;
    const item = data.getLastAggregate.length > 0 ? data.getLastAggregate[0] : [{ GwId: null }];
    const result = item as unknown as { GwId: string };
    const loggerId = result.GwId;

    await this.addLoggerToSolarmanByLoggerSerial(loggerId);
  }

  async addLoggerToSolarmanByLoggerSerial(loggerId: string) {
    const url = '/device-s/device/add-device';
    const body = {
      language: AUTH.language,
      deviceType: 'COLLECTOR',
      snList: [loggerId],
    };

    await this.callApiSolarman(url, body, 'callPROSolarman');
  }

  async deviceModbusCmdRequest(cmd: Uint8Array, deviceId: string) {
    const content = Array.from(cmd).map((value) => value.toString(16).padStart(2, '0')).join('');
    const url = '/device/v1.0/customControl';
    const body = {
      deviceSN: deviceId,
      callBackUrl: 'https://3jzp43hftf.execute-api.eu-west-1.amazonaws.com/devices_stage/deviceId',
      content,
    };

    const result = await this.callApiSolarman(url, body);
    smLog(`SEND - 0x${Array.from(cmd).map((x) => x.toString(16).padStart(2, '0')).join(' ')}`);
    return JSON.parse(result) as CallAPISolarmanModbusResponse;
  }

  async callApiSolarman(url: string, body: object, api = 'callAPISolarman', method = 'POST', qParams = {}): Promise<string> {
    smLog(`GraphQL ${api} ${method} ${url}`);
    const queryParams= {
      appId: AUTH.appId,
      language: AUTH.language,
      ...qParams,
    };

    const query = gql`
    query ${api}($url: String!, $method: String!, $headers: String, $body: AWSJSON, $queryParams: AWSJSON) {
      ${api}(url: $url, method: $method, headers: $headers, body: $body, queryParams: $queryParams)
    }`;

    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = 'network-only';
    const { data } = await client.query<{ [key: string ]: Array<Partial<object>> }>({
      query,
      fetchPolicy,
      variables: {
        url,
        method,
        headers: this.credentials?.access_token,
        body: JSON.stringify(body),
        queryParams: JSON.stringify(queryParams),
      },
    });

    const resp = data[api] as unknown as string;
    smLog(`${method} ${url} → resp ` + (typeof resp === 'string' ? resp.slice(0, 240) : JSON.stringify(resp)?.slice(0, 240)));
    return resp;
  }


  async getInverterSerialFromLogger(loggerId: string): Promise<string> {
    let deviceParams;

    try {
      deviceParams = await this.deviceInfoRequest(`${loggerId}`);
      if (!deviceParams?.communication.childList?.length) {
        throw new Error('Logger not found');
      }
    } catch (error) {
      try {
        await this.addLoggerToSolarmanByLoggerSerial(loggerId);
        deviceParams = await this .deviceInfoRequest(`${loggerId}`);
        if (!deviceParams?.communication.childList?.length) {
          throw new Error('Logger not found');
        }
      } catch {
        throw new Error('Logger not found');
      }
    }

    return deviceParams.communication.childList[0].deviceSn;
  }
}
